// /prospectos/:id — tenant workspace: a prospect's full record (mockup 23, view 2).
// Header + stage strip + activity timeline + contact details. The cita control
// (scheduleAppointment), add-note (addLeadNote) and "Asignado a" (assignLead)
// write through to the API. Data via the getLead() seam -> GET /leads/:id.
//
// IT IS ITS OWN ROUTE, NOT A DRAWER. A card on the board navigates here, so the
// board leaves the screen — and the only thing pointing back was a breadcrumb
// that renders its single item as PLAIN TEXT (PageHeader never links the last
// crumb), i.e. no way back at all short of the browser button. CP-1 F-A adds
// the real one: a "Volver al tablero" button that lands on the card's OWN
// process (`/prospectos?proceso=…`), not on whatever funnel the board defaults
// to. The same payload field also lets the ficha SAY which process it is in.
//
// THE CITA CONTROL SAYS WHAT IT DOES. It is one endpoint with two outcomes, and
// it used to describe only one of them: the button always read "Fecha de
// seguimiento" and the modal always promised, in a hardcoded string, that the
// prospect moved to "Cita agendada" — a stage name this file invented. A process
// that renamed that stage was lied to, and a process that marks NO commitment
// stage was lied to twice (the card does not move at all). The copy is derived
// from `commitmentStageName` now, and the saved date is rendered on the record
// instead of disappearing into the database.

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { type ReactNode, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { EMPTY_FICHA_HINT, LeadFieldInputs } from './lead-fields-form.js'
import {
  addLeadNote,
  type ActivityKind,
  assignLead,
  getLead,
  linkLeadContact,
  scheduleAppointment,
  updateLeadFields,
} from './leads-data.js'
import {
  Button,
  Chip,
  EmptyStateCard,
  FormField,
  Input,
  Modal,
  PageHeader,
  Select,
  Spinner,
  Textarea,
  toast,
} from '../../design-system/index.js'
import { apiErrorMessage } from '../../lib/api-error.js'
import { formatMxPhone } from '../../lib/phone.js'
import { QueryError } from '../../lib/QueryError.js'
import { createContact, getContacts } from '../contacts/contacts-data.js'
import { useAssignableMembers } from '../oversight/useAssignableMembers.js'

const ACTIVITY: Record<ActivityKind, { tone: string; path: ReactNode }> = {
  created: { tone: 'bg-hover text-text-2', path: <path d="M12 5v14M5 12h14" /> },
  bot: {
    tone: 'bg-hover text-text-2',
    path: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z" />,
  },
  assigned: {
    tone: 'bg-hover text-text-2',
    path: (
      <>
        <circle cx="12" cy="8" r="4" />
        <path d="M4 21c0-4 4-6 8-6s8 2 8 6" />
      </>
    ),
  },
  note: {
    tone: 'bg-warn-bg text-warn',
    path: <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />,
  },
  stage: { tone: 'bg-ok-bg text-ok', path: <path d="M20 6 9 17l-5-5" /> },
}

function MetaRow({ k, children }: { k: string; children: ReactNode }): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-2.5 last:border-b-0">
      <span className="text-[11.5px] text-text-2">{k}</span>
      <span className="text-right text-[12px] font-semibold text-text">{children}</span>
    </div>
  )
}

export function LeadDetailPage(): JSX.Element {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const {
    data: lead,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['lead', id],
    queryFn: () => getLead(id),
  })
  const [apptOpen, setApptOpen] = useState(false)
  const [apptAt, setApptAt] = useState('')
  const [apptBusy, setApptBusy] = useState(false)
  const [note, setNote] = useState('')
  const [noteBusy, setNoteBusy] = useState(false)
  const [assignBusy, setAssignBusy] = useState(false)
  const [linkOpen, setLinkOpen] = useState(false)
  const [linkQuery, setLinkQuery] = useState('')
  // A6: lo que se escribe y lo que se pregunta al servidor son dos cosas. El
  // selector pedía /contacts en CADA tecla, y como la clave de la consulta
  // lleva el término, la respuesta de una búsqueda vieja podía pintarse encima
  // de la nueva. Con el término ya reposado, react-query solo tiene una
  // consulta activa y las viejas quedan huérfanas.
  const [linkTerm, setLinkTerm] = useState('')
  const [linkBusy, setLinkBusy] = useState(false)
  // El alta manual dentro del propio selector (CP-1 F-B): el directorio solo
  // ofrecía gente que YA nos había escrito, así que la primera vez que alguien
  // llega por teléfono o en persona no había a quién vincular.
  const [createOpen, setCreateOpen] = useState(false)
  const [newName, setNewName] = useState('')
  const [newPhone, setNewPhone] = useState('')
  const [createBusy, setCreateBusy] = useState(false)
  // Los datos del catálogo, tal como los va editando la persona. Se resiembran
  // con cada respuesta del servidor (guardar recarga la ficha).
  const [fieldVals, setFieldVals] = useState<Record<string, string>>({})
  const [fieldsBusy, setFieldsBusy] = useState(false)
  // The tenant's contacts, searched the same way Contactos does (same endpoint,
  // same `q`). Only asked for while the picker is open.
  const { data: contactOptions, isLoading: contactsLoading } = useQuery({
    queryKey: ['contacts', linkTerm],
    queryFn: () => getContacts(linkTerm || undefined),
    enabled: linkOpen,
  })

  useEffect(() => {
    const timer = setTimeout(() => setLinkTerm(linkQuery.trim()), 250)
    return () => clearTimeout(timer)
  }, [linkQuery])
  // Fetched with the page, not on demand: the control itself depends on whether
  // there is anybody to hand the prospect to.
  const { members: assignables, canReassign } = useAssignableMembers()

  useEffect(() => {
    if (lead) setFieldVals(lead.fieldValues ?? {})
  }, [lead])

  async function refresh(): Promise<void> {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['lead', id] }),
      qc.invalidateQueries({ queryKey: ['leads'] }),
    ])
  }

  // La etapa de compromiso de ESTE proceso, tal como se llama, o null si el
  // proceso no marca ninguna. Todo lo que el control dice sale de aquí.
  const commitmentName = lead?.commitmentStageName ?? null
  const apptTitle = commitmentName ? 'Agendar cita' : 'Fecha de seguimiento'
  const apptRowLabel = commitmentName ? 'Cita' : 'Seguimiento'

  async function saveAppointment(): Promise<void> {
    if (!apptAt) {
      toast.error('Falta la fecha', { description: 'Elige la fecha y la hora.' })
      return
    }
    setApptBusy(true)
    try {
      // datetime-local has no timezone; treat it as local and send an ISO string.
      await scheduleAppointment(id, new Date(apptAt).toISOString())
      await refresh()
      toast.ok(commitmentName ? 'Cita agendada' : 'Fecha de seguimiento guardada')
      setApptOpen(false)
      setApptAt('')
    } catch {
      toast.error('No se pudo guardar la fecha', { description: 'Inténtalo de nuevo.' })
    } finally {
      setApptBusy(false)
    }
  }

  /** Hand the prospect to somebody, or to nobody (''). */
  async function reassign(memberId: string): Promise<void> {
    setAssignBusy(true)
    try {
      await assignLead(id, memberId || null)
      await refresh()
      const to = assignables.find((m) => m.id === memberId)
      toast.ok(to ? `Prospecto asignado a ${to.name}` : 'Prospecto sin asignar')
    } catch {
      toast.error('No se pudo asignar', { description: 'Inténtalo de nuevo.' })
    } finally {
      setAssignBusy(false)
    }
  }

  /** Cierra el selector y lo deja limpio: la búsqueda y el alta a medio escribir
   *  no deben seguir ahí la próxima vez que se abra. */
  function closeLink(): void {
    setLinkOpen(false)
    setLinkQuery('')
    setCreateOpen(false)
    setNewName('')
    setNewPhone('')
  }

  /** Alta manual + vínculo en un solo gesto (CP-1 F-B). Crear a la persona y
   *  tener que buscarla después sería el mismo callejón, con más pasos. */
  async function createAndLink(): Promise<void> {
    const name = newName.trim()
    if (!name) {
      toast.error('Falta el nombre', { description: 'Escribe el nombre de la persona.' })
      return
    }
    setCreateBusy(true)
    try {
      const created = await createContact(name, newPhone.trim() || undefined)
      await linkLeadContact(id, created.id)
      await refresh()
      toast.ok('Contacto creado y vinculado')
      closeLink()
    } catch (err) {
      // El servidor tiene motivos con nombre (teléfono repetido, tarjeta ya
      // abierta): se leen tal cual, no "inténtalo de nuevo".
      toast.error('No se pudo crear el contacto', {
        description: apiErrorMessage(err, 'Inténtalo de nuevo.'),
      })
    } finally {
      setCreateBusy(false)
    }
  }

  /** Tie the prospect to the picked contact (F24). */
  async function linkContact(contactId: string): Promise<void> {
    setLinkBusy(true)
    try {
      await linkLeadContact(id, contactId)
      await refresh()
      toast.ok('Contacto vinculado')
      closeLink()
    } catch (err) {
      // W1-6 made this refusable for a REASON (open_card_conflict: la persona ya
      // tiene tarjeta abierta en este proceso). A hardcoded "intentalo de nuevo"
      // hides the one message that says what to do instead.
      toast.error('No se pudo vincular', {
        description: apiErrorMessage(err, 'Inténtalo de nuevo.'),
      })
    } finally {
      setLinkBusy(false)
    }
  }

  /** Guarda los datos del catálogo (F26). Se manda TODO el formulario, con los
   *  vacíos incluidos: dejar un campo en blanco es borrar esa respuesta, y
   *  omitirlo la dejaría intacta para siempre. */
  async function saveFields(): Promise<void> {
    setFieldsBusy(true)
    try {
      await updateLeadFields(id, fieldVals)
      await refresh()
      toast.ok('Datos guardados')
    } catch (err) {
      toast.error('No se pudieron guardar', {
        description: apiErrorMessage(err, 'Inténtalo de nuevo.'),
      })
    } finally {
      setFieldsBusy(false)
    }
  }

  async function submitNote(): Promise<void> {
    if (!note.trim()) return
    setNoteBusy(true)
    try {
      await addLeadNote(id, note.trim())
      await refresh()
      setNote('')
    } catch {
      toast.error('No se pudo guardar la nota', { description: 'Inténtalo de nuevo.' })
    } finally {
      setNoteBusy(false)
    }
  }

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-4xl py-10">
        <Spinner label="Cargando prospecto…" />
      </div>
    )
  }

  // A fetch error must NOT read as "prospecto no encontrado" (the empty state
  // below) — surface the error with a retry.
  if (isError) {
    return (
      <div className="mx-auto w-full max-w-2xl py-10">
        <QueryError message="No pudimos cargar este prospecto." onRetry={() => void refetch()} />
      </div>
    )
  }

  if (!lead) {
    return (
      <div className="mx-auto w-full max-w-2xl">
        <PageHeader title="Prospecto no encontrado" />
        <div className="mt-6">
          <EmptyStateCard
            icon={
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden
              >
                <path d="M3 6h18M3 12h18M3 18h12" strokeLinecap="round" />
              </svg>
            }
            title="No encontramos este prospecto"
            body="Puede que el enlace sea incorrecto o que haya cambiado de etapa."
            actions={
              <Button onClick={() => void navigate('/prospectos')}>Volver a prospectos</Button>
            }
          />
        </div>
      </div>
    )
  }

  const currentIndex = lead.stages.findIndex((s) => s.id === lead.stageId)
  // El tablero del que salió esta tarjeta: el de SU proceso, con el mismo
  // parámetro que usa el selector del tablero. Sin proceso conocido (una ficha
  // vieja en caché) se cae al tablero por defecto, que sigue siendo una salida.
  const boardHref = lead.pipeline
    ? `/prospectos?proceso=${encodeURIComponent(lead.pipeline.id)}`
    : '/prospectos'

  return (
    <div data-testid="lead-detail" className="mx-auto w-full max-w-4xl">
      {/* CP-1 F-A: el regreso al tablero, dicho con todas sus letras. La miga
          de pan de PageHeader dibuja su último elemento como TEXTO, así que
          "Procesos" nunca fue un enlace: desde la ficha no se podía volver. */}
      <Button
        variant="ghost"
        size="sm"
        className="-ml-[10px] mb-1"
        onClick={() => void navigate(boardHref)}
        iconStart={
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="m15 18-6-6 6-6" />
          </svg>
        }
      >
        Volver al tablero
      </Button>
      <PageHeader breadcrumb={[{ label: lead.pipeline?.name ?? 'Procesos' }]} title={lead.name} />

      <div className="mt-4 flex flex-wrap items-start gap-3">
        <span
          aria-hidden
          className="grid size-11 flex-none place-items-center rounded-full bg-[image:var(--brand-gradient)] text-[14px] font-bold text-white"
        >
          {lead.name
            .split(' ')
            .map((w) => w[0])
            .slice(0, 2)
            .join('')}
        </span>
        <div className="min-w-0 flex-1">
          <Chip tone={lead.specialty.tone} dot>
            {lead.specialty.label}
          </Chip>
        </div>
        <div className="flex flex-none gap-2">
          <Button variant="secondary" size="sm" onClick={() => setApptOpen(true)}>
            {apptTitle}
          </Button>
        </div>
      </div>

      <Modal
        open={apptOpen}
        onClose={() => setApptOpen(false)}
        title={apptTitle}
        subtitle={
          commitmentName
            ? `Guarda la fecha acordada con el prospecto. El prospecto pasa a la etapa “${commitmentName}”. No crea una cita en la Agenda.`
            : 'Guardamos la fecha. El prospecto no cambia de etapa. No crea una cita en la Agenda.'
        }
        footer={
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setApptOpen(false)}
              disabled={apptBusy}
            >
              Cancelar
            </Button>
            <Button
              size="sm"
              className="ml-auto"
              disabled={apptBusy}
              onClick={() => void saveAppointment()}
            >
              {apptBusy ? 'Guardando…' : 'Guardar fecha'}
            </Button>
          </>
        }
      >
        <FormField label="Fecha y hora" htmlFor="appt-at">
          <Input
            id="appt-at"
            type="datetime-local"
            value={apptAt}
            onChange={(e) => setApptAt(e.target.value)}
          />
        </FormField>
      </Modal>

      {/* F24: el selector de contacto. Busca contra el directorio real (el
          mismo endpoint que Contactos) y vincula al elegir — sin paso extra. */}
      <Modal
        open={linkOpen}
        onClose={closeLink}
        title="Vincular contacto"
        subtitle="Busca a la persona en tus contactos; al elegirla queda vinculada a este prospecto."
      >
        <div className="flex flex-col gap-3">
          <Input
            aria-label="Buscar contacto"
            placeholder="Buscar por nombre o teléfono…"
            value={linkQuery}
            onChange={(e) => setLinkQuery(e.target.value)}
          />
          {contactsLoading || !contactOptions ? (
            <Spinner label="Buscando contactos…" />
          ) : contactOptions.length === 0 ? (
            <p className="rounded-lg border border-line bg-surface-2 px-3 py-6 text-center text-[12px] text-text-3">
              Ningún contacto coincide.
            </p>
          ) : (
            <ul className="max-h-[260px] divide-y divide-line overflow-y-auto rounded-lg border border-line">
              {contactOptions.map((c) => {
                // A4: un renglón NUNCA se titula con un número. El servidor
                // resuelve `displayName` con la misma cadena de respaldo que
                // todo lo demás, y esa cadena termina en el teléfono, así que
                // una persona a la que nunca le supimos el nombre llegaba aquí
                // con su wa_id de trece dígitos como título: una lista de
                // números imposible de elegir con confianza. El título es el
                // nombre o "Sin nombre"; el teléfono baja al renglón de abajo,
                // ya legible, junto al canal por el que se le alcanza.
                const phone = c.phone.trim()
                const rawName = c.displayName.trim()
                const name = rawName && rawName !== phone ? rawName : ''
                const label = name || 'Sin nombre'
                const meta = [phone ? formatMxPhone(phone) : '', c.channelLabel]
                  .filter(Boolean)
                  .join(' · ')
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      disabled={linkBusy}
                      onClick={() => void linkContact(c.id)}
                      className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left transition-colors hover:bg-hover focus-visible:outline focus-visible:-outline-offset-2 focus-visible:outline-2 focus-visible:outline-brand-blue disabled:opacity-60"
                    >
                      <span
                        className={`min-w-0 truncate text-[12.5px] font-semibold ${
                          name ? 'text-text' : 'text-text-2'
                        }`}
                      >
                        {label}
                      </span>
                      {meta ? (
                        <span className="flex-none text-[11.5px] text-text-3">{meta}</span>
                      ) : null}
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
          {/* CP-1 F-B: el alta manual. El selector solo ofrecía gente que ya
              nos había escrito, así que a la primera persona que llega por
              teléfono o en persona no había manera de vincularla nunca. */}
          {createOpen ? (
            <div className="flex flex-col gap-3 rounded-lg border border-line bg-surface-2 p-3">
              <FormField label="Nombre" htmlFor="new-contact-name">
                <Input
                  id="new-contact-name"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Nombre de la persona"
                />
              </FormField>
              <FormField label="Teléfono (opcional)" htmlFor="new-contact-phone">
                <Input
                  id="new-contact-phone"
                  value={newPhone}
                  onChange={(e) => setNewPhone(e.target.value)}
                  placeholder="+52 55 1234 5678"
                />
              </FormField>
              <div className="flex justify-end gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={createBusy}
                  onClick={() => setCreateOpen(false)}
                >
                  Cancelar
                </Button>
                <Button size="sm" disabled={createBusy} onClick={() => void createAndLink()}>
                  {createBusy ? 'Creando…' : 'Crear y vincular'}
                </Button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => {
                // Lo que la persona ya escribió buscando es, casi siempre, el
                // nombre que quería dar de alta.
                setNewName(linkQuery.trim())
                setCreateOpen(true)
              }}
              className="self-start text-[12px] font-semibold text-link hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue"
            >
              Crear contacto nuevo
            </button>
          )}
        </div>
      </Modal>

      {/* stage strip */}
      <ol className="mt-4 flex gap-1.5">
        {lead.stages.map((s, i) => {
          const done = i < currentIndex
          const current = i === currentIndex
          return (
            <li
              key={s.id}
              aria-current={current ? 'step' : undefined}
              className={`flex-1 rounded-lg border px-2 py-1.5 text-center text-[10.5px] font-semibold ${
                current
                  ? 'border-brand-blue bg-brand-blue text-white'
                  : done
                    ? 'border-line bg-surface text-text-2'
                    : 'border-line bg-surface-2 text-text-3'
              }`}
            >
              <span
                className={`block text-[8.5px] font-bold uppercase tracking-[0.05em] ${
                  current ? 'text-white' : 'text-text-3'
                }`}
              >
                Etapa {i + 1}
                {current ? ' · actual' : ''}
              </span>
              {s.name}
            </li>
          )
        })}
      </ol>

      <div className="mt-4 grid grid-cols-1 items-start gap-4 lg:grid-cols-[1fr,280px]">
        {/* timeline */}
        <section className="rounded-xl border border-line bg-surface">
          <h2 className="border-b border-line px-4 py-3 text-[12.5px] font-bold text-text">
            Actividad
          </h2>
          <ol className="px-4 py-2">
            {lead.activity.map((a, i) => {
              const cfg = ACTIVITY[a.kind]
              const last = i === lead.activity.length - 1
              return (
                <li key={a.id} className="relative flex gap-3 py-2.5">
                  {!last && (
                    <span
                      aria-hidden
                      className="absolute bottom-[-10px] left-[11px] top-7 w-px bg-line"
                    />
                  )}
                  <span
                    aria-hidden
                    className={`z-10 grid size-[23px] flex-none place-items-center rounded-full [&_svg]:size-[12px] ${cfg.tone}`}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      {cfg.path}
                    </svg>
                  </span>
                  <div className="min-w-0 flex-1 pt-0.5">
                    <p className="text-[12px] leading-snug text-text">{a.text}</p>
                    {a.note && (
                      <p className="mt-1.5 rounded-lg border border-line bg-surface-2 px-2.5 py-2 text-[11.5px] leading-relaxed text-text-2">
                        {a.note}
                      </p>
                    )}
                    <p className="mt-0.5 text-[10.5px] text-text-3">{a.time}</p>
                  </div>
                </li>
              )
            })}
          </ol>
          <div className="flex flex-col gap-2 border-t border-line px-4 py-3">
            <Textarea
              aria-label="Agregar una nota"
              placeholder="Agregar una nota sobre este prospecto…"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <div className="flex justify-end">
              <Button
                size="sm"
                disabled={noteBusy || !note.trim()}
                onClick={() => void submitNote()}
              >
                {noteBusy ? 'Guardando…' : 'Agregar nota'}
              </Button>
            </div>
          </div>
        </section>

        {/* details */}
        <aside className="rounded-xl border border-line bg-surface">
          <h2 className="border-b border-line px-4 py-3 text-[12.5px] font-bold text-text">
            Detalles
          </h2>
          {/* Un prospecto es de alguien. Sin esta fila, la ficha era una isla:
              el teléfono estaba a la vista pero el expediente de la persona, con
              sus otras conversaciones y citas, no se alcanzaba desde aquí. */}
          <MetaRow k="Contacto">
            {lead.contact ? (
              <span className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => void navigate(`/contactos/${lead.contact?.id ?? ''}`)}
                  className="text-link hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue"
                >
                  {lead.contact.name}
                </button>
                {/* F24: el vínculo se puede corregir sin desvincular — el
                    selector reemplaza, nunca deja al prospecto sin persona. */}
                <button
                  type="button"
                  onClick={() => setLinkOpen(true)}
                  className="font-normal text-[11px] text-text-3 hover:text-link hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue"
                >
                  Cambiar
                </button>
              </span>
            ) : (
              <span className="flex items-center justify-end gap-2">
                <span className="font-normal text-text-3">Sin contacto vinculado</span>
                {/* F24: sin esta salida, un prospecto sin contacto no tenía
                    forma de conseguir uno. */}
                <button
                  type="button"
                  onClick={() => setLinkOpen(true)}
                  className="text-link hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue"
                >
                  Vincular contacto
                </button>
              </span>
            )}
          </MetaRow>
          <MetaRow k="Teléfono">{lead.phone}</MetaRow>
          {/* A5: era "Canal", y salía de `lead.channel` — una columna que nadie
              escribe nunca y que por omisión dice 'whatsapp', así que juraba
              que una persona que entró caminando había escrito por WhatsApp.
              Esto se DERIVA de las identidades de canal del contacto: si no
              tiene ninguna (o no hay contacto), la alta fue a mano y lo dice. */}
          <MetaRow k="Llegó por">{lead.arrivedBy}</MetaRow>
          {/* CP-1 F-A: una etapa solo significa algo DENTRO de su embudo, y la
              ficha decía "Calificado" sin decir jamás calificado de qué. */}
          {lead.pipeline ? <MetaRow k="Proceso">{lead.pipeline.name}</MetaRow> : null}
          <MetaRow k="Etapa">{lead.stages[currentIndex]?.name ?? '—'}</MetaRow>
          {/* La fecha guardada, a la vista. Se escribía y no se dibujaba en
              ninguna parte de la ficha: quien la acababa de capturar no tenía
              cómo confirmarla, ni cómo saber cuál estaba puesta antes de
              cambiarla. El tablero ya la decía en la tarjeta; el expediente no. */}
          {lead.appointmentAt ? (
            <MetaRow k={apptRowLabel}>
              <span className="flex items-center justify-end gap-2">
                {lead.appointmentAt}
                <button
                  type="button"
                  onClick={() => setApptOpen(true)}
                  className="font-normal text-[11px] text-text-3 hover:text-link hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue"
                >
                  Cambiar
                </button>
              </span>
            </MetaRow>
          ) : null}
          {/* Solo mode: with only yourself on the team there is nobody to hand
              the prospecto to, so the picker doesn't render (same rule, same
              hook, as Reasignar in Bandeja). */}
          <MetaRow k="Asignado a">
            {canReassign ? (
              <span className="block w-[150px]">
                <Select
                  aria-label="Asignar prospecto"
                  value={lead.assigneeId ?? ''}
                  disabled={assignBusy}
                  onChange={(e) => void reassign(e.target.value)}
                  options={[
                    { value: '', label: 'Sin asignar' },
                    ...assignables.map((m) => ({ value: m.id, label: m.name })),
                  ]}
                />
              </span>
            ) : (
              (lead.assignee ?? 'Sin asignar')
            )}
          </MetaRow>
          {/* A5: la otra mitad, y la única que escribe una persona: qué trajo
              a este prospecto. Solo se dibuja cuando alguien la escribió; el
              renglón con "—" ocupaba lugar para un dato opcional por diseño. */}
          {lead.origin ? <MetaRow k="Cómo nos conoció">{lead.origin}</MetaRow> : null}
          <MetaRow k="Creado">{lead.created}</MetaRow>
          {/* F26 — Datos. Era una lista de solo lectura llamada "Datos
              capturados por el agente": si el Agente no lo preguntó, no había
              manera de escribirlo, y una etapa que lo exigía dejaba la tarjeta
              atorada sin salida. Ahora es la ficha del proceso, con su tipo y
              editable.

              CP3 L: el ORDEN lo decide el servidor y llega ya resuelto — los
              datos que la etapa actual obtiene o exige vienen primero, el resto
              detrás, en el orden de la ficha. Sin encabezados y sin ocultar
              nada: es un acomodo, no una sección aparte. */}
          {lead.fields && lead.fields.length > 0 ? (
            <div
              data-testid="lead-fields"
              className="flex flex-col gap-3 border-t border-line px-4 py-3"
            >
              <p className="text-[10.5px] font-bold uppercase tracking-[0.05em] text-text-3">
                Datos
              </p>
              <LeadFieldInputs
                fields={lead.fields}
                values={fieldVals}
                idPrefix="lead-detail-field"
                disabled={fieldsBusy}
                onChange={(key, value) => setFieldVals((prev) => ({ ...prev, [key]: value }))}
              />
              <div className="flex justify-end">
                <Button size="sm" disabled={fieldsBusy} onClick={() => void saveFields()}>
                  {fieldsBusy ? 'Guardando…' : 'Guardar datos'}
                </Button>
              </div>
            </div>
          ) : (
            // Un proceso que no define datos no dibuja la sección Datos, y su
            // ausencia no se distingue de un error de carga. Se dice, con la
            // misma frase del modal de alta y para todos los roles: a un Asesor
            // no le hace daño saber dónde vive la configuración.
            <p
              data-testid="lead-fields-empty"
              className="border-t border-line px-4 py-3 text-[12px] leading-relaxed text-text-2"
            >
              {EMPTY_FICHA_HINT}
            </p>
          )}
          {/* Respuestas guardadas cuya clave el catálogo ya no conoce: se ven,
              no se editan. Un dato que capturó el Agente no se pierde porque el
              campo se renombró u ocultó. */}
          {lead.uncatalogued && lead.uncatalogued.length > 0 ? (
            <>
              <p className="border-t border-line px-4 pb-1 pt-3 text-[10.5px] font-bold uppercase tracking-[0.05em] text-text-3">
                Otros datos capturados
              </p>
              {lead.uncatalogued.map((f) => (
                <MetaRow key={f.label} k={f.label}>
                  {f.value}
                </MetaRow>
              ))}
            </>
          ) : null}
          {lead.conversationId ? (
            <button
              type="button"
              onClick={() =>
                void navigate(`/bandeja?c=${encodeURIComponent(lead.conversationId ?? '')}`)
              }
              className="flex w-full items-center gap-2 px-4 py-3 text-[12px] font-semibold text-link transition-colors hover:bg-hover"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.1"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
                className="size-[15px]"
              >
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z" />
              </svg>
              Ver la conversación
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
                className="ml-auto size-[14px]"
              >
                <path d="m9 6 6 6-6 6" />
              </svg>
            </button>
          ) : null}
        </aside>
      </div>
    </div>
  )
}
